import type { ComponentPropsWithRef } from "react";
import { useMemo } from "react";
import { Button } from "@/common/components-2/button";
import { Checkbox } from "@/common/components-2/checkbox";
import { Input } from "@/common/components-2/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, useHookFormAction } from "@/services/forms";
import { useSetHubSpotClientContextValues } from "@/services/hub-spot/use-set-hub-spot-client-context-values";
import { useTranslations } from "@/services/i18n";

import { sendContactFormAction } from "../../actions/send-contact-form";
import { contactFormDefaultValues, contactFormSchema } from "../../models/contact-form";

interface ContactFormProps extends Omit<ComponentPropsWithRef<"form">, "onSubmit"> {}

export function ContactForm(props: ContactFormProps) {
  const t = useTranslations("pages.hrano.contact.form");
  const tModels = useTranslations("pages.hrano.models.contact_form");
  const schema = useMemo(() => contactFormSchema(tModels), [tModels]);

  const { form, action, handleSubmitWithAction } = useHookFormAction(sendContactFormAction, schema, {
    actionProps: {
      onSuccess: () => {
        form.reset();
      },
    },
    formProps: {
      defaultValues: contactFormDefaultValues,
    },
    errorMapProps: {},
  });

  useSetHubSpotClientContextValues(form);

  return (
    <Form {...form}>
      <form onSubmit={handleSubmitWithAction} {...props}>
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem className="md:col-start-1 md:col-end-3 lg:col-start-1 lg:col-end-2">
              <FormLabel>{t("fields.name.label")} *</FormLabel>
              <FormControl>
                <Input
                  placeholder={t("fields.name.placeholder")}
                  className="border-stone-400 bg-transparent"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem className="md:col-start-1 md:col-end-3 lg:col-start-2 lg:col-end-3">
              <FormLabel>{t("fields.lastName.label")} *</FormLabel>
              <FormControl>
                <Input
                  placeholder={t("fields.lastName.placeholder")}
                  className="border-stone-400 bg-transparent"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="md:col-start-1 md:col-end-3 lg:col-start-1 lg:col-end-2">
              <FormLabel>{t("fields.email.label")} *</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder={t("fields.email.placeholder")}
                  className="border-stone-400 bg-transparent"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem className="md:col-start-1 md:col-end-3 lg:col-start-2 lg:col-end-3">
              <FormLabel>{t("fields.phone.label")} *</FormLabel>
              <FormControl>
                <Input
                  type="tel"
                  placeholder={t("fields.phone.placeholder")}
                  className="border-stone-400 bg-transparent"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="company"
          render={({ field }) => (
            <FormItem className="md:col-start-1 md:col-end-3 lg:col-start-1 lg:col-end-2">
              <FormLabel>{t("fields.company.label")} *</FormLabel>
              <FormControl>
                <Input
                  placeholder={t("fields.company.placeholder")}
                  className="border-stone-400 bg-transparent"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="employees"
          render={({ field }) => (
            <FormItem className="md:col-start-1 md:col-end-3 lg:col-start-2 lg:col-end-3">
              <FormLabel>{t("fields.employees.label")} *</FormLabel>
              <FormControl>
                <Input
                  placeholder={t("fields.employees.placeholder")}
                  className="border-stone-400 bg-transparent"
                  {...field}
                  value={field.value as string}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="terms"
          render={({ field }) => (
            <FormItem className="col-start-1 col-end-3 flex flex-row items-start gap-3">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-0.5" />
              </FormControl>
              <div className="flex flex-col gap-1">
                <FormLabel className="font-normal text-muted-foreground leading-5 [&_a]:text-dodger-blue-700 [&_a]:underline">
                  {t.rich("fields.terms.label")}
                </FormLabel>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="privacy"
          render={({ field }) => (
            <FormItem className="col-start-1 col-end-3 flex flex-row items-start gap-3">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-0.5" />
              </FormControl>
              <div className="flex flex-col gap-1">
                <FormLabel className="font-normal text-muted-foreground leading-5 [&_a]:text-dodger-blue-700 [&_a]:underline">
                  {t.rich("fields.privacy.label")}
                </FormLabel>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />
        <div className="col-start-1 col-end-3 w-full">
          <Button size="lg" type="submit">
            {t("submit")}
          </Button>
          {action.hasErrored ? (
            <p className="mt-2 font-medium text-destructive text-sm">{t("send_result.error")}</p>
          ) : null}
          {action.hasSucceeded ? (
            <p className="mt-2 font-medium text-emerald-700 text-sm">{t("send_result.success")}</p>
          ) : null}
        </div>
      </form>
    </Form>
  );
}
